// Página pública de auto-registro / aceitação de convite.
//
// Dois modos:
//   - Modo invite: URL traz ?token=… → email + embarcador vêm do convite
//     (read-only). Não usa captcha; nome de usuário autopopulado pelo email.
//   - Modo self-register: sem token → gated por hub.users.allowSelfRegistration.
//     Mantém captcha e fluxo original.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Container,
  Col,
  Row,
} from "reactstrap";
import { Link, useHistory, useLocation } from "react-router-dom";
import NotificationAlert from "react-notification-alert";

import { Form } from "@unform/web";
import * as Yup from "yup";
import logo from "../../../assets/img/autopilot-logo.png";
import getValidationErrors from "../../../utils/getValidationErrors";
import CustomInputGroupText from "components/CustomInputGroupText";
import { useToast } from "../../../hooks/toast";
import {
  getRegistrationStatus,
  getCaptcha,
  registerUser,
} from "services/registrationApi";
import { previewInvite, acceptInvite } from "services/invitesApi";
import "../Login/login.css";

function classesPresentes(senha) {
  let n = 0;
  if (/[a-z]/.test(senha)) n += 1;
  if (/[A-Z]/.test(senha)) n += 1;
  if (/[0-9]/.test(senha)) n += 1;
  if (/[^A-Za-z0-9]/.test(senha)) n += 1;
  return n;
}

function useQueryToken() {
  const location = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("token");
  }, [location.search]);
}

const Register = () => {
  const formRef = useRef(null);
  const alertRef = useRef();
  const { addToast } = useToast();
  const history = useHistory();
  const token = useQueryToken();

  const [status, setStatus] = useState({ loaded: false });
  const [captcha, setCaptcha] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [invite, setInvite] = useState(null); // { email, embarcadorNome, expiresAt }
  const [inviteError, setInviteError] = useState(null);

  const refreshCaptcha = useCallback(async () => {
    try {
      const c = await getCaptcha();
      setCaptcha(c);
    } catch (err) {
      console.error("[Register] getCaptcha:", err);
    }
  }, []);

  // Carga inicial: prioriza token (modo invite), senão consulta o status
  // de self-register.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (token) {
        try {
          const preview = await previewInvite(token);
          if (cancelled) return;
          setInvite(preview);
          setStatus({ loaded: true });
        } catch (err) {
          if (cancelled) return;
          const errStatus = err.response && err.response.status;
          const data = (err.response && err.response.data) || {};
          setInviteError(
            errStatus === 410
              ? "Este convite expirou ou já foi utilizado. Solicite um novo ao seu administrador."
              : data.error || "Convite inválido."
          );
          setStatus({ loaded: true });
        }
        return;
      }

      try {
        const s = await getRegistrationStatus();
        if (cancelled) return;
        setStatus({ loaded: true, ...s });
        if (s.allowSelfRegistration && s.useCaptchaOnRegistration) {
          await refreshCaptcha();
        }
      } catch (err) {
        if (cancelled) return;
        console.error("[Register] status:", err);
        setStatus({ loaded: true, allowSelfRegistration: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, refreshCaptcha]);

  const handleSubmit = useCallback(
    async (data) => {
      try {
        formRef.current.setErrors({});

        // Schema comum (senha + confirmação)
        const baseShape = {
          name: Yup.string().required("Nome é obrigatório"),
          userName: Yup.string().required("Nome de usuário é obrigatório"),
          password: Yup.string()
            .required("Senha é obrigatória")
            .min(8, "Mínimo 8 caracteres")
            .test(
              "complexidade",
              "Use 3 das 4 classes (minúscula, maiúscula, número, símbolo)",
              (v) => classesPresentes(v || "") >= 3
            ),
          confirmacao: Yup.string()
            .required("Confirme a senha")
            .oneOf([Yup.ref("password")], "As senhas não coincidem"),
        };

        // Campos extras se for self-register (não invite)
        const schema = invite
          ? Yup.object().shape(baseShape)
          : Yup.object().shape({
              ...baseShape,
              email: Yup.string()
                .email("E-mail inválido")
                .required("E-mail é obrigatório"),
              captchaAnswer: status.useCaptchaOnRegistration
                ? Yup.string().required("Responda o captcha")
                : Yup.string(),
            });

        await schema.validate(data, { abortEarly: false });
        setSubmitting(true);

        if (invite) {
          await acceptInvite({
            token,
            name: data.name,
            surname: data.surname || null,
            userName: data.userName,
            password: data.password,
          });
          addToast({
            place: "tr",
            type: "success",
            title: "Conta criada",
            description: "Você já pode fazer login com suas credenciais.",
            icon: "tim-icons icon-check-2",
          });
        } else {
          const result = await registerUser({
            name: data.name,
            surname: data.surname || null,
            email: data.email,
            userName: data.userName,
            password: data.password,
            captchaToken: captcha ? captcha.token : undefined,
            captchaAnswer: data.captchaAnswer,
          });
          addToast({
            place: "tr",
            type: "success",
            title: "Conta criada",
            description: result.requiresEmailConfirmation
              ? "Verifique seu e-mail para confirmar a conta antes de fazer login."
              : "Você já pode fazer login com suas credenciais.",
            icon: "tim-icons icon-check-2",
          });
        }

        setTimeout(() => history.push("/auth/login"), 1800);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRef.current.setErrors(errors);
          return;
        }
        const errData = (error.response && error.response.data) || {};
        const errStatus = error.response && error.response.status;
        let description = errData.error || "Não foi possível criar a conta.";
        if (errStatus === 409 && errData.field === "email") {
          description = "Este e-mail já está em uso.";
        } else if (errStatus === 409 && errData.field === "userName") {
          description = "Este nome de usuário já está em uso.";
        } else if (errStatus === 400 && errData.field === "captcha") {
          description = "Captcha inválido — tente novamente.";
        } else if (errStatus === 410) {
          description = "Convite expirado ou já utilizado.";
        } else if (errStatus === 403) {
          description = "Auto-registro está desabilitado.";
        }
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro no registro",
          description,
          icon: "tim-icons icon-alert-circle-exc",
        });
        if ((errStatus === 400 || errStatus === 409) && !invite) {
          refreshCaptcha();
        }
      } finally {
        setSubmitting(false);
      }
    },
    [invite, token, status, captcha, history, addToast, refreshCaptcha]
  );

  const logoContainer = (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(255, 255, 255, 0.92)",
        borderRadius: "16px",
        width: "160px",
        height: "80px",
        marginBottom: "16px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
      }}
    >
      <img
        alt="Everlog"
        src={logo}
        style={{ width: "140px", objectFit: "contain" }}
      />
    </div>
  );

  if (!status.loaded) {
    return (
      <div className="content">
        <Container>
          <Col className="ml-auto mr-auto" lg="4" md="6">
            <Card className="glass-card">
              <CardBody>
                <p style={{ color: "#fff", textAlign: "center", margin: 0 }}>
                  Carregando…
                </p>
              </CardBody>
            </Card>
          </Col>
        </Container>
      </div>
    );
  }

  // Modo invite: token presente mas inválido/expirado
  if (token && inviteError) {
    return (
      <div className="content">
        <Container>
          <Col className="ml-auto mr-auto" lg="4" md="6">
            <Card className="glass-card">
              <CardHeader>
                {logoContainer}
                <h4>Convite indisponível</h4>
              </CardHeader>
              <CardBody>
                <p style={{ color: "rgba(255,255,255,0.75)", textAlign: "center" }}>
                  {inviteError}
                </p>
              </CardBody>
              <CardFooter>
                <div style={{ textAlign: "center" }}>
                  <Link
                    to="/auth/login"
                    style={{ color: "#fff", fontSize: 13 }}
                  >
                    Voltar para o login
                  </Link>
                </div>
              </CardFooter>
            </Card>
          </Col>
        </Container>
      </div>
    );
  }

  // Modo self-register sem token: bloqueia se setting estiver off
  if (!invite && !status.allowSelfRegistration) {
    return (
      <div className="content">
        <Container>
          <Col className="ml-auto mr-auto" lg="4" md="6">
            <Card className="glass-card">
              <CardHeader>
                {logoContainer}
                <h4>Registro indisponível</h4>
              </CardHeader>
              <CardBody>
                <p style={{ color: "rgba(255,255,255,0.75)", textAlign: "center" }}>
                  O auto-registro está desabilitado. Entre em contato com um
                  administrador para solicitar acesso.
                </p>
              </CardBody>
              <CardFooter>
                <div style={{ textAlign: "center" }}>
                  <Link
                    to="/auth/login"
                    style={{ color: "#fff", fontSize: 13 }}
                  >
                    Voltar para o login
                  </Link>
                </div>
              </CardFooter>
            </Card>
          </Col>
        </Container>
      </div>
    );
  }

  // Form: modo invite ou self-register aberto
  const initialData = invite
    ? {
        email: invite.email,
        userName: (invite.email || "").split("@")[0] || "",
      }
    : undefined;

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={alertRef} />
      </div>
      <div className="content">
        <Container>
          <Col className="ml-auto mr-auto" lg="5" md="7">
            <Form
              ref={formRef}
              onSubmit={handleSubmit}
              initialData={initialData}
            >
              <Card className="glass-card">
                <CardHeader>
                  {logoContainer}
                  <h4>
                    {invite ? (
                      <>
                        Concluir cadastro na <strong>Everlog</strong>
                      </>
                    ) : (
                      <>
                        Criar conta na <strong>Everlog</strong>
                      </>
                    )}
                  </h4>
                  {invite && invite.embarcadorNome && (
                    <div
                      style={{
                        marginTop: 8,
                        padding: "6px 12px",
                        background: "rgba(29,140,248,0.18)",
                        border: "1px solid rgba(29,140,248,0.4)",
                        borderRadius: 6,
                        fontSize: 13,
                        color: "#fff",
                        textAlign: "center",
                      }}
                    >
                      Convite para <strong>{invite.embarcadorNome}</strong>
                    </div>
                  )}
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md="6">
                      <CustomInputGroupText
                        name="name"
                        placeholder="Nome *"
                        type="text"
                        addonType="prepend"
                        icon="tim-icons icon-single-02"
                      />
                    </Col>
                    <Col md="6">
                      <CustomInputGroupText
                        name="surname"
                        placeholder="Sobrenome"
                        type="text"
                        addonType="prepend"
                        icon="tim-icons icon-single-02"
                      />
                    </Col>
                  </Row>
                  <CustomInputGroupText
                    name="email"
                    placeholder="E-mail *"
                    type="email"
                    addonType="prepend"
                    icon="tim-icons icon-email-85"
                    disabled={!!invite}
                  />
                  <CustomInputGroupText
                    name="userName"
                    placeholder="Nome de usuário *"
                    type="text"
                    addonType="prepend"
                    icon="tim-icons icon-badge"
                  />
                  <CustomInputGroupText
                    name="password"
                    placeholder="Senha *"
                    type="password"
                    addonType="prepend"
                    icon="tim-icons icon-lock-circle"
                  />
                  <CustomInputGroupText
                    name="confirmacao"
                    placeholder="Confirmar senha *"
                    type="password"
                    addonType="prepend"
                    icon="tim-icons icon-lock-circle"
                  />
                  <p
                    style={{
                      color: "rgba(255,255,255,0.5)",
                      fontSize: 11,
                      marginTop: 4,
                      marginBottom: 12,
                    }}
                  >
                    Mínimo 8 caracteres + 3 das 4 classes (minúscula, maiúscula,
                    número, símbolo).
                  </p>

                  {!invite && status.useCaptchaOnRegistration && captcha && (
                    <div
                      style={{
                        background: "rgba(255,255,255,0.07)",
                        border: "1px solid rgba(255,255,255,0.15)",
                        borderRadius: 8,
                        padding: "10px 12px",
                        marginBottom: 12,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: 6,
                          color: "rgba(255,255,255,0.9)",
                        }}
                      >
                        <span style={{ fontFamily: "monospace", fontSize: 16 }}>
                          {captcha.question}
                        </span>
                        <button
                          type="button"
                          onClick={refreshCaptcha}
                          style={{
                            background: "none",
                            border: "none",
                            color: "rgba(99,160,255,0.9)",
                            cursor: "pointer",
                            fontSize: 12,
                          }}
                        >
                          Gerar novo
                        </button>
                      </div>
                      <CustomInputGroupText
                        name="captchaAnswer"
                        placeholder="Resposta"
                        type="text"
                        addonType="prepend"
                        icon="tim-icons icon-puzzle-10"
                      />
                    </div>
                  )}
                </CardBody>
                <CardFooter>
                  <Button
                    block
                    className="btn-entrar mb-3"
                    size="lg"
                    type="submit"
                    disabled={submitting}
                  >
                    {submitting ? "Criando conta…" : "Criar conta"}
                  </Button>
                  <div style={{ textAlign: "center" }}>
                    <Link
                      to="/auth/login"
                      style={{ color: "#fff", fontSize: 13 }}
                    >
                      Já tem conta? Entrar
                    </Link>
                  </div>
                </CardFooter>
              </Card>
            </Form>
          </Col>
        </Container>
      </div>
    </>
  );
};

export default Register;
